import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {radius, weight} from '../scale.js';

export type WedgeBv2PartsLook = 'bv2part-chip' | 'bv2part-primary' | 'bv2part-copy';

const BV2_PARTS_LOOKS = new Set<string>(['bv2part-chip', 'bv2part-primary', 'bv2part-copy']);
export const isWedgeBv2PartsLook = (look: string): look is WedgeBv2PartsLook => BV2_PARTS_LOOKS.has(look);

/* Released brief v2 small parts (prep-brief-v2 and its kit), off-scale. The contact chip is a 36px pill (a link or a button) with 12px side
   padding, a 1px border, a filled field, 12.5px semibold type and no underline. The primary button is 46px high with 20px side padding, a
   14px radius, no border and 15px semibold type; enabled it takes the accent fill, the app ink and a glow, disabled it takes the field fill,
   the muted ink, no glow and the default cursor. The copy button is 34px high with 12px side padding, a 10px radius, a 1px border, a field
   fill, the secondary ink and 13px type at weight 500. The caller sets the font family and points the --wedge-bv2-* roles at the brief scope
   vars; the app also hands in the primary ink and glow (--wedge-bv2-primary-ink and --wedge-bv2-primary-glow). Outside the brief each role
   falls back to a base token. All three boxes are below 44px in the shipped screen and had no transparent target there. No look draws one
   (wedgeBv2PartsDrawsHit): the chips sit in a wrapping row 8px apart, and an anchor would add a positioned box that legacy does not have. */
const role = (name: string, fallback: string): string => `var(--wedge-bv2-${name}, ${fallback})`;
export const wedgeBv2PartsDrawsHit = (look: string): boolean => !isWedgeBv2PartsLook(look);

const row = {display: 'inline-flex', alignItems: 'center'} as const;

export function wedgeBv2PartsStyle(look: WedgeBv2PartsLook, disabled = false): CSSProperties {
  if (look === 'bv2part-chip') {
    return {
      ...row, gap: 6, minHeight: 36, padding: '7px 12px', borderRadius: radius.pill,
      border: `1px solid ${role('line', T.separator)}`, background: role('card2', T.surfaceAlt), color: role('text', T.label),
      fontSize: 12.5, fontWeight: weight.semibold, textDecoration: 'none', cursor: 'pointer',
    };
  }
  if (look === 'bv2part-primary') {
    return {
      ...row, justifyContent: 'center', gap: 8, height: 46, padding: '0 20px', borderRadius: radius.xl, border: 'none',
      cursor: disabled ? 'default' : 'pointer',
      background: disabled ? role('field', T.surfaceAlt) : role('acc', T.accent),
      color: disabled ? role('muted', T.labelSec) : role('primary-ink', T.label),
      boxShadow: disabled ? 'none' : role('primary-glow', 'none'),
      fontSize: 15, fontWeight: weight.semibold,
    };
  }
  return {
    ...row, gap: 6, height: 34, padding: '0 12px', borderRadius: radius.md, border: `1px solid ${role('line', T.separator)}`,
    background: role('card2', T.surfaceAlt), color: role('sec', T.labelSec), fontSize: 13, fontWeight: weight.medium, cursor: 'pointer', whiteSpace: 'nowrap',
  };
}
